import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, History, Info, Plus, RefreshCw, Search, Trash2, TrendingUp } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, usePermission, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton, Tip } from "@/components/ui";
import { api, ApiError, type ProductCostItem, type ProfitManualCost, type ProfitManualCostInput, type ProfitReport, type ProfitSettings } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { intlLocale, msg, t, tn } from "@/lib/i18n";

const money = (minor: number | null | undefined, digits = 2) => (minor == null ? "—" : (minor / 100).toLocaleString(intlLocale(), { style: "currency", currency: "EUR", minimumFractionDigits: digits, maximumFractionDigits: digits }));
const unitEur = (v: number | null | undefined) => (v == null ? "—" : v.toLocaleString(intlLocale(), { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 4 }));
const pct = (v: number | null | undefined) => (v == null ? "—" : `${v.toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} %`);
const dmy = (s: string) => new Date(s.length === 10 ? `${s}T12:00:00Z` : s).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit" });
const berlinDay = (d: Date) => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin" }).format(d);
const errText = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? t("Fehler")) : t("Fehler"));
/** Betrag fürs Eingabefeld: 2–4 Nachkommastellen, ohne Tausenderpunkte */
const fieldEur = (v: number | null) => (v == null ? "" : v.toLocaleString(intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 4, useGrouping: false }));
/** Eingabe „12,50“ oder „12.5“ → Zahl */
const parseEur = (s: string) => {
  const v = Number(s.trim().replace(/\s|€/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", "."));
  return s.trim() && Number.isFinite(v) && v >= 0 ? v : null;
};

const TABS = [["overview", msg("Übersicht")], ["products", msg("Artikel")], ["costs", msg("Einstandspreise")], ["extra", msg("Weitere Kosten")], ["settings", msg("Gebühren & Abgleich")]] as const;
type Tab = (typeof TABS)[number][0];
const PERIODS = [[7, msg("7 Tage")], [30, msg("30 Tage")], [90, msg("90 Tage")], [365, msg("12 Monate")]] as const;

/** Profit Center: Deckungsbeitrag je Bestellung, Artikel und Kanal (Modul „profit“, eigenes Recht) */
export function ProfitPage() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find(([k]) => k === params.get("tab"))?.[0] ?? "overview") as Tab;
  const may = usePermission();
  if (!may("profit", "view"))
    return (
      <div className="pb-16">
        <PageHeader title={t("Profit Center")} />
        <EmptyState icon={<TrendingUp className="size-5" />} title={t("Kein Zugriff")}>{t("Das Profit Center sehen nur Personen mit dem Recht „Profit Center“ – bitte an einen Administrator wenden.")}</EmptyState>
      </div>
    );
  return (
    <div className="pb-16">
      <PageHeader title={t("Profit Center")} description={t("Was nach Ware, Gebühren und Fulfillment wirklich übrig bleibt – je Bestellung, Artikel und Kanal. Alle Beträge netto.")} />
      <div className="px-4 md:px-8 space-y-4">
        <div className="max-w-full overflow-x-auto">
          <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" role="tablist">
            {TABS.map(([k, label]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams(k === "overview" ? {} : { tab: k }, { replace: true })} className={cx("h-8 px-4 rounded-md text-[13px] font-medium whitespace-nowrap", tab === k ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
                {t(label)}
              </button>
            ))}
          </div>
        </div>
        {tab === "overview" && <Overview onCosts={() => setParams({ tab: "costs", missing: "1" }, { replace: true })} />}
        {tab === "products" && <ProductsPanel />}
        {tab === "costs" && <CostsPanel />}
        {tab === "extra" && <ExtraCostsPanel />}
        {tab === "settings" && <SettingsPanel />}
      </div>
    </div>
  );
}

function usePeriod() {
  const [days, setDays] = useState<number>(30);
  const [channelId, setChannelId] = useState<string>("");
  const to = berlinDay(new Date());
  const from = berlinDay(new Date(Date.now() - (days - 1) * 86_400_000));
  return { days, setDays, channelId, setChannelId, from, to };
}

function PeriodBar({ p, channels }: { p: ReturnType<typeof usePeriod>; channels: { id: string; name: string }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border">
        {PERIODS.map(([d, label]) => (
          <button key={d} onClick={() => p.setDays(d)} aria-pressed={p.days === d} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", p.days === d ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>{t(label)}</button>
        ))}
      </div>
      {channels.length > 1 && (
        <select value={p.channelId} onChange={(e) => p.setChannelId(e.target.value)} className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]" aria-label={t("Kanal")}>
          <option value="">{t("Alle Kanäle")}</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
    </div>
  );
}

function useReport(p: ReturnType<typeof usePeriod>) {
  const { tenantId } = useTenant();
  return useQuery({ queryKey: ["profit", tenantId, p.from, p.to, p.channelId], queryFn: () => api.profit.report(tenantId, { from: p.from, to: p.to, ...(p.channelId ? { channelId: p.channelId } : {}) }), staleTime: 60_000, retry: false });
}

function Overview({ onCosts }: { onCosts: () => void }) {
  const p = usePeriod();
  const q = useReport(p);
  const r = q.data;
  if (q.isError) return <Card className="p-5 text-[13px]">{errText(q.error)}</Card>;
  return (
    <div className="space-y-4 max-w-6xl">
      <PeriodBar p={p} channels={r?.channels.map((c) => ({ id: c.channelId, name: c.name })) ?? []} />
      {!r ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : r.totals.orders === 0 ? (
        <EmptyState icon={<TrendingUp className="size-5" />} title={t("Keine Bestellungen im Zeitraum")}>{t("Sobald Bestellungen eingehen, steht hier, was jede einbringt.")}</EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label={t("Umsatz netto")} value={money(r.totals.revenueNetMinor, 0)} sub={tn(r.totals.orders, "{n} Bestellung", "{n} Bestellungen", { n: fmtNumber(r.totals.orders) })} />
            <Kpi label={t("Deckungsbeitrag")} value={money(r.totals.contributionMinor, 0)} sub={t("{p} vom Umsatz", { p: pct(r.totals.marginPct) })} tone={r.totals.contributionMinor < 0 ? "danger" : "success"} />
            <Kpi label={t("Wareneinsatz")} value={money(r.totals.cogsMinor, 0)} sub={t("Einstandspreis × Menge")} />
            <Kpi label={t("Je Bestellung")} value={money(Math.round(r.totals.contributionMinor / r.totals.orders))} sub={t("Ø Deckungsbeitrag")} />
          </div>
          <Notes r={r} onCosts={onCosts} />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <Card className="p-5 lg:col-span-3">
              <h2 className="font-semibold text-[14px]">{t("Je Tag")}</h2>
              <p className="text-[12px] text-fg-subtle mb-3">{t("Umsatz netto, dunkel: Deckungsbeitrag")}</p>
              <DayChart days={r.days} />
            </Card>
            <Card className="p-5 lg:col-span-2">
              <h2 className="font-semibold text-[14px] mb-3">{t("Vom Umsatz zum Deckungsbeitrag")}</h2>
              <Waterfall r={r} />
            </Card>
          </div>
          {r.channels.length > 1 && (
            <Card className="overflow-hidden">
              <h2 className="font-semibold text-[14px] px-5 pt-4 pb-2">{t("Je Kanal")}</h2>
              <table className="w-full text-[13px]">
                <thead className="text-[12px] text-fg-muted"><tr className="border-b border-border"><th className="text-left font-medium px-5 py-2">{t("Kanal")}</th><th className="text-right font-medium px-3">{t("Bestellungen")}</th><th className="text-right font-medium px-3">{t("Umsatz netto")}</th><th className="text-right font-medium px-3 hidden md:table-cell">{t("Gebühren")}</th><th className="text-right font-medium px-3">{t("Deckungsbeitrag")}</th><th className="text-right font-medium px-5">{t("Marge")}</th></tr></thead>
                <tbody>
                  {r.channels.map((c) => (
                    <tr key={c.channelId} className="border-b border-border last:border-0">
                      <td className="px-5 py-2.5 font-medium">{c.name}</td>
                      <td className="text-right px-3 tabular-nums">{fmtNumber(c.orders)}</td>
                      <td className="text-right px-3 tabular-nums">{money(c.revenueNetMinor, 0)}</td>
                      <td className="text-right px-3 tabular-nums hidden md:table-cell">{money(c.feesMinor, 0)}</td>
                      <td className={cx("text-right px-3 tabular-nums font-medium", c.contributionMinor < 0 && "text-danger")}>{money(c.contributionMinor, 0)}</td>
                      <td className="text-right px-5 tabular-nums">{pct(c.marginPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
          <WeakOrders r={r} />
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: "success" | "danger" }) {
  return (
    <Card className="p-4">
      <p className="text-[12.5px] text-fg-muted">{t(label)}</p>
      <p className={cx("mt-1 text-[22px] font-semibold tabular-nums", tone === "danger" && "text-danger")}>{value}</p>
      <p className="text-[12px] text-fg-subtle">{t(sub)}</p>
    </Card>
  );
}

function Notes({ r, onCosts }: { r: ProfitReport; onCosts: () => void }) {
  const notes: { key: string; text: string; action?: { label: string; run: () => void } }[] = [];
  if (r.missingCost.length) notes.push({ key: "cost", text: tn(r.missingCost.length, "Für {n} verkauften Artikel fehlt der Einstandspreis – der Deckungsbeitrag ist dort zu hoch.", "Für {n} verkaufte Artikel fehlt der Einstandspreis – der Deckungsbeitrag ist dort zu hoch.", { n: r.missingCost.length }), action: { label: t("Preise ergänzen"), run: onCosts } });
  if (r.totals.estimatedOrders) notes.push({ key: "est", text: tn(r.totals.estimatedOrders, "{n} Bestellung ohne Steuerangaben aus dem Kanal – netto mit dem Standard-Steuersatz geschätzt.", "{n} Bestellungen ohne Steuerangaben aus dem Kanal – netto mit dem Standard-Steuersatz geschätzt.", { n: fmtNumber(r.totals.estimatedOrders) }) });
  if (r.fulfillmentNote) notes.push({ key: "ful", text: t(r.fulfillmentNote) });
  else if (!r.fulfillmentAvailable) notes.push({ key: "ful", text: t("Ohne Fulfillment-Kosten: die Kosten je Auftrag aus eurer Logiful-Abrechnung schaltet Logiful auf Wunsch frei.") });
  if (!notes.length) return null;
  return (
    <div className="space-y-1.5">
      {notes.map((n) => (
        <div key={n.key} className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface-2/50 px-3.5 py-2 text-[12.5px]">
          <Info className="size-3.5 text-fg-subtle shrink-0" />
          <span className="flex-1 min-w-0">{t(n.text)}</span>
          {n.action && <Button size="sm" variant="secondary" onClick={n.action.run}>{t(n.action.label)}</Button>}
        </div>
      ))}
    </div>
  );
}

function DayChart({ days }: { days: ProfitReport["days"] }) {
  const max = Math.max(1, ...days.map((d) => d.revenueNetMinor));
  return (
    <div className="h-44 flex items-end gap-[2px]">
      {days.map((d) => {
        const share = d.revenueNetMinor > 0 ? Math.max(0, d.contributionMinor) / d.revenueNetMinor : 0;
        return (
          <Tip key={d.day} content={`${dmy(d.day)}: ${t("Umsatz")} ${money(d.revenueNetMinor, 0)} · ${t("Deckungsbeitrag")} ${money(d.contributionMinor, 0)}`}>
            <div className="flex-1 min-w-0 h-full flex flex-col justify-end">
              <div className="w-full rounded-t-[2px] bg-ink/15 flex flex-col justify-end overflow-hidden" style={{ height: `${Math.max(2, (d.revenueNetMinor / max) * 100)}%` }}>
                <div className={cx("w-full", d.contributionMinor < 0 ? "bg-danger/70" : "bg-ink/75")} style={{ height: `${d.contributionMinor < 0 ? 100 : share * 100}%` }} />
              </div>
            </div>
          </Tip>
        );
      })}
    </div>
  );
}

function Waterfall({ r }: { r: ProfitReport }) {
  const tt = r.totals;
  const gross = tt.revenueNetMinor;
  const rows: { label: string; minor: number; neg?: boolean; strong?: boolean; tip?: string }[] = [
    { label: t("Umsatz netto"), minor: gross, tip: t("Nach Rabatten, ohne Steuer, abzüglich Erstattungen ({x})", { x: money(tt.refundsNetMinor, 0) }) },
    { label: t("Wareneinsatz"), minor: tt.cogsMinor, neg: true },
    { label: t("Gebühren"), minor: tt.feesMinor, neg: true, tip: t("Zahlungsanbieter und Marktplätze – je Kanal unter „Gebühren & Abgleich“") },
    ...(r.fulfillmentIncluded ? [{ label: t("Fulfillment"), minor: tt.fulfillmentMinor, neg: true, tip: t("Je versendetem Auftrag aus eurer Logiful-Abrechnung") }] : []),
    ...(tt.otherMinor ? [{ label: t("Weitere Kosten je Auftrag"), minor: tt.otherMinor, neg: true, tip: t("Von euch erfasst – unter „Weitere Kosten“") }] : []),
    { label: t("Deckungsbeitrag"), minor: tt.contributionMinor, strong: true },
    ...(tt.fixedCostsMinor ? [
      { label: t("Fixkosten (anteilig)"), minor: tt.fixedCostsMinor, neg: true, tip: r.fixedCosts.map((c) => `${c.label}: ${money(c.periodMinor, 0)}`).join(" · ") },
      { label: t("Ergebnis nach Fixkosten"), minor: tt.resultMinor, strong: true },
    ] : []),
  ];
  const max = Math.max(1, gross);
  return (
    <div className="space-y-2.5">
      {rows.map((x) => (
        <div key={x.label} className="text-[13px]">
          <div className="flex items-center justify-between gap-2">
            <span className={cx(x.strong ? "font-semibold" : "text-fg-muted", "flex items-center gap-1")}>{t(x.label)}{x.tip && <Tip content={x.tip}><Info className="size-3 text-fg-subtle" /></Tip>}</span>
            <span className={cx("tabular-nums", x.strong && "font-semibold", x.strong && x.minor < 0 && "text-danger")}>{x.neg ? "− " : ""}{money(x.minor, 0)}</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-surface-2 overflow-hidden">
            <div className={cx("h-full rounded-full", x.strong ? (x.minor < 0 ? "bg-danger" : "bg-success") : x.neg ? "bg-ink/35" : "bg-ink/75")} style={{ width: `${Math.min(100, (Math.abs(x.minor) / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function WeakOrders({ r }: { r: ProfitReport }) {
  const [all, setAll] = useState(false);
  const may = usePermission();
  const canEdit = may("profit", "edit");
  const withOther = r.orders.some((o) => o.otherMinor > 0);
  const [adding, setAdding] = useState<ProfitReport["orders"][number] | null>(null);
  const rows = all ? r.orders : r.orders.slice(0, 10);
  const name = new Map(r.channels.map((c) => [c.channelId, c.name]));
  return (
    <Card className="overflow-hidden">
      <div className="px-5 pt-4 pb-2">
        <h2 className="font-semibold text-[14px]">{t("Schwächste Bestellungen")}</h2>
        <p className="text-[12px] text-fg-subtle">{t("Wo Versand, Rabatte oder Gebühren den Gewinn auffressen")}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] min-w-[640px]">
          <thead className="text-[12px] text-fg-muted"><tr className="border-b border-border"><th className="text-left font-medium px-5 py-2">{t("Bestellung")}</th><th className="text-right font-medium px-3">{t("Umsatz netto")}</th><th className="text-right font-medium px-3">{t("Ware")}</th><th className="text-right font-medium px-3">{t("Gebühren")}</th>{r.fulfillmentIncluded && <th className="text-right font-medium px-3">{t("Fulfillment")}</th>}{withOther && <th className="text-right font-medium px-3">{t("Weitere")}</th>}<th className="text-right font-medium px-5">{t("Deckungsbeitrag")}</th>{canEdit && <th className="w-10" />}</tr></thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.key} className="border-b border-border last:border-0">
                <td className="px-5 py-2">
                  <span className="font-medium">{o.orderNumber}</span>
                  <span className="block text-[11.5px] text-fg-subtle">{dmy(o.placedAt)} · {name.get(o.channelId) ?? ""}{!o.exact && <> · <Tip content={t("Ohne Steuerangaben aus dem Kanal – netto geschätzt")}><span className="underline decoration-dotted">{t("geschätzt")}</span></Tip></>}{o.refundsNetMinor > 0 && <> · {t("Erstattung {x}", { x: money(o.refundsNetMinor) })}</>}</span>
                </td>
                <td className="text-right px-3 tabular-nums">{money(o.revenueNetMinor)}</td>
                <td className="text-right px-3 tabular-nums">{money(o.cogsMinor)}{o.missingCostSkus.length > 0 && <Tip content={t("Ohne Einstandspreis: {skus}", { skus: o.missingCostSkus.join(", ") })}><AlertTriangle className="inline size-3 ml-1 text-warning" /></Tip>}</td>
                <td className="text-right px-3 tabular-nums">{money(o.feesMinor)}</td>
                {r.fulfillmentIncluded && <td className="text-right px-3 tabular-nums">{money(o.fulfillmentMinor)}</td>}
                {withOther && <td className="text-right px-3 tabular-nums">{o.otherMinor ? money(o.otherMinor) : "—"}</td>}
                <td className={cx("text-right px-5 tabular-nums font-medium", o.contributionMinor < 0 && "text-danger")}>{money(o.contributionMinor)}<span className="block text-[11.5px] font-normal text-fg-subtle">{pct(o.marginPct)}</span></td>
                {canEdit && <td className="pr-3"><button onClick={() => setAdding(o)} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" title={t("Kosten zu dieser Bestellung erfassen")} aria-label={t("Kosten zu dieser Bestellung erfassen")}><Plus className="size-3.5" /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {r.orders.length > 10 && <div className="px-5 py-2.5 border-t border-border"><button className="text-[12.5px] text-fg-muted hover:text-fg" onClick={() => setAll(!all)}>{all ? t("Weniger anzeigen") : t("Alle {n} anzeigen", { n: r.orders.length })}</button></div>}
      {adding && <CostDialog kind="order" order={{ id: adding.orderId, number: adding.orderNumber }} onClose={() => setAdding(null)} />}
    </Card>
  );
}

function ProductsPanel() {
  const p = usePeriod();
  const q = useReport(p);
  const [search, setSearch] = useState("");
  const r = q.data;
  if (q.isError) return <Card className="p-5 text-[13px]">{errText(q.error)}</Card>;
  const s = search.trim().toLowerCase();
  const rows = (r?.products ?? []).filter((x) => !s || x.sku.toLowerCase().includes(s) || x.name.toLowerCase().includes(s));
  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex flex-wrap items-center gap-2">
        <PeriodBar p={p} channels={r?.channels.map((c) => ({ id: c.channelId, name: c.name })) ?? []} />
        <div className="ml-auto w-full sm:w-64"><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("SKU oder Name …")} leading={<Search className="size-3.5" />} /></div>
      </div>
      <Card className="overflow-hidden">
        <p className="px-5 pt-4 pb-2 text-[12px] text-fg-subtle">{t("Deckungsbeitrag je Artikel = Netto-Umsatz der Position − Wareneinsatz (vor Gebühren und Fulfillment, die je Bestellung anfallen).")}</p>
        {!r ? <div className="p-5"><Skeleton className="h-40" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] min-w-[620px]">
              <thead className="text-[12px] text-fg-muted"><tr className="border-b border-border"><th className="text-left font-medium px-5 py-2">{t("Artikel")}</th><th className="text-right font-medium px-3">{t("Menge")}</th><th className="text-right font-medium px-3">{t("Umsatz netto")}</th><th className="text-right font-medium px-3">{t("Wareneinsatz")}</th><th className="text-right font-medium px-3">{t("Deckungsbeitrag")}</th><th className="text-right font-medium px-5">{t("Marge")}</th></tr></thead>
              <tbody>
                {rows.map((x) => (
                  <tr key={x.productId ?? x.sku} className="border-b border-border last:border-0">
                    <td className="px-5 py-2"><span className="font-medium">{x.name}</span><span className="block text-[11.5px] text-fg-subtle font-mono">{x.sku}</span></td>
                    <td className="text-right px-3 tabular-nums">{fmtNumber(x.units)}</td>
                    <td className="text-right px-3 tabular-nums">{money(x.revenueNetMinor)}</td>
                    <td className="text-right px-3 tabular-nums">{x.cogsMinor == null ? <Badge tone="warning">{t("Preis fehlt")}</Badge> : money(x.cogsMinor)}</td>
                    <td className={cx("text-right px-3 tabular-nums font-medium", (x.contributionMinor ?? 0) < 0 && "text-danger")}>{money(x.contributionMinor)}</td>
                    <td className="text-right px-5 tabular-nums">{pct(x.marginPct)}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={6} className="px-5 py-6 text-center text-fg-subtle">{t("Keine Verkäufe im Zeitraum")}</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

const SOURCE: Record<NonNullable<ProductCostItem["source"]>, string> = { average: msg("Ø aus Wareneingängen"), purchase: msg("Einkaufspreis (Nachbestellung)"), bundle: msg("Summe der Set-Bestandteile") };

function CostsPanel() {
  const { tenantId } = useTenant();
  const may = usePermission();
  const canEdit = may("profit", "edit");
  const [params] = useSearchParams();
  const [missing, setMissing] = useState(params.get("missing") === "1");
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => { const h = setTimeout(() => setTerm(search.trim()), 250); return () => clearTimeout(h); }, [search]);
  const q = useQuery({ queryKey: ["profit-costs", tenantId, term, missing], queryFn: () => api.profit.costs(tenantId, { search: term, missing }) });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="space-y-4 max-w-5xl">
      <Card className="p-4 text-[12.5px] text-fg-muted flex gap-2">
        <Info className="size-4 shrink-0 text-fg-subtle mt-0.5" />
        <span>{t("Der Einstandspreis (netto je Stück) wird mit jedem gebuchten Wareneingang als gleitender Durchschnitt fortgeschrieben: Bestand zum bisherigen Preis plus Eingang zum Preis der Anlieferung. Den Preis je Anlieferung übernehmen wir aus euren Bestellungen oder ihr tragt ihn unten ein. Versendete Aufträge behalten den Preis vom Versandtag.")}</span>
      </Card>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-72"><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("SKU oder Name …")} leading={<Search className="size-3.5" />} /></div>
        <label className="inline-flex items-center gap-2 text-[13px]"><input type="checkbox" checked={missing} onChange={(e) => setMissing(e.target.checked)} />{t("Nur ohne Einstandspreis")}</label>
      </div>
      <Card className="overflow-hidden">
        {!q.data ? <div className="p-5"><Skeleton className="h-40" /></div> : (
          <table className="w-full text-[13px]">
            <thead className="text-[12px] text-fg-muted"><tr className="border-b border-border"><th className="text-left font-medium px-5 py-2">{t("Artikel")}</th><th className="text-left font-medium px-3 hidden md:table-cell">{t("Herkunft")}</th><th className="text-right font-medium px-5 w-48">{t("Einstandspreis")}</th></tr></thead>
            <tbody>
              {q.data.items.map((x) => (
                <Fragment key={x.id}>
                  <tr className="border-b border-border">
                    <td className="px-5 py-2">
                      <button className="text-left inline-flex items-start gap-1.5" onClick={() => setOpen(open === x.id ? null : x.id)} aria-expanded={open === x.id}>
                        <ChevronDown className={cx("size-3.5 mt-1 text-fg-subtle transition-transform", open !== x.id && "-rotate-90")} />
                        <span><span className="font-medium">{x.name}</span><span className="block text-[11.5px] text-fg-subtle font-mono">{x.sku}</span></span>
                      </button>
                    </td>
                    <td className="px-3 hidden md:table-cell text-[12px] text-fg-muted">{x.source ? t(SOURCE[x.source]) : <Badge tone="warning">{t("fehlt")}</Badge>}{x.costUpdatedAt && <span className="block text-fg-subtle">{fmtRelative(x.costUpdatedAt)}</span>}</td>
                    <td className="px-5 py-2 text-right">{canEdit ? <CostInput item={x} /> : <span className="tabular-nums">{unitEur(x.effectiveCost)}</span>}</td>
                  </tr>
                  {open === x.id && <tr className="border-b border-border bg-surface-2/40"><td colSpan={3} className="px-5 py-3"><CostHistory productId={x.id} /></td></tr>}
                </Fragment>
              ))}
              {!q.data.items.length && <tr><td colSpan={3} className="px-5 py-6 text-center text-fg-subtle">{missing ? t("Alle Artikel haben einen Einstandspreis.") : t("Keine Artikel gefunden")}</td></tr>}
            </tbody>
          </table>
        )}
      </Card>
      <InboundCosts canEdit={canEdit} />
    </div>
  );
}

function CostInput({ item }: { item: ProductCostItem }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const initial = fieldEur(item.effectiveCost);
  const [v, setV] = useState(initial);
  useEffect(() => setV(initial), [initial]);
  const save = useMutation({
    mutationFn: (cost: number) => api.profit.setCost(tenantId, item.id, cost),
    onSuccess: () => { toast.success(t("Einstandspreis gespeichert")); void qc.invalidateQueries({ queryKey: ["profit-costs", tenantId] }); void qc.invalidateQueries({ queryKey: ["profit", tenantId] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const commit = () => {
    if (v === initial) return;
    const n = parseEur(v);
    if (n == null) { toast.error(t("Bitte einen Betrag eingeben, z. B. 12,50")); setV(initial); return; }
    save.mutate(n);
  };
  return <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} inputMode="decimal" placeholder="0,00" suffix="€" className="text-right tabular-nums" aria-label={t("Einstandspreis {sku}", { sku: item.sku })} disabled={save.isPending} invalid={item.effectiveCost == null} />;
}

function CostHistory({ productId }: { productId: string }) {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["profit-cost-history", tenantId, productId], queryFn: () => api.profit.costHistory(tenantId, productId) });
  if (!q.data) return <Skeleton className="h-10" />;
  if (!q.data.items.length) return <p className="text-[12.5px] text-fg-subtle">{t("Noch keine Änderungen.")}</p>;
  return (
    <ul className="space-y-1 text-[12.5px]">
      <li className="flex items-center gap-1.5 text-fg-muted font-medium"><History className="size-3.5" />{t("Verlauf")}</li>
      {q.data.items.map((e) => (
        <li key={e.id} className="flex flex-wrap gap-x-2 tabular-nums">
          <span className="text-fg-subtle w-24">{new Date(e.createdAt).toLocaleDateString(intlLocale())}</span>
          {e.kind === "inbound"
            ? <span>{t("Wareneingang {nr}: {qty} Stk. zu {price} (Bestand vorher {stock})", { nr: e.inboundNumber ?? "", qty: fmtNumber(e.quantity ?? 0), price: unitEur(e.unitCost), stock: fmtNumber(e.stockBefore ?? 0) })}</span>
            : <span>{t("Von Hand gesetzt")}</span>}
          <span className="text-fg-muted">{unitEur(e.costBefore)} → <span className="font-medium text-fg">{unitEur(e.costAfter)}</span></span>
        </li>
      ))}
    </ul>
  );
}

function InboundCosts({ canEdit }: { canEdit: boolean }) {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["profit-inbounds", tenantId], queryFn: () => api.profit.inbounds(tenantId) });
  const [open, setOpen] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  return (
    <Card className="overflow-hidden">
      <div className="px-5 pt-4 pb-2">
        <h2 className="font-semibold text-[14px]">{t("Einstandspreise je Wareneingang")}</h2>
        <p className="text-[12px] text-fg-subtle">{t("Preis je Stück inklusive Fracht und Zoll, wenn ihr die mitrechnen wollt. Bereits gebuchte Mengen werden sofort eingerechnet.")}</p>
      </div>
      {!q.data ? <div className="p-5"><Skeleton className="h-20" /></div> : !items.length ? <p className="px-5 pb-4 text-[12.5px] text-fg-subtle">{t("Noch keine Wareneingänge.")}</p> : (
        <ul>
          {items.map((ib) => {
            const missingN = ib.lines.filter((l) => l.unitCost == null).length;
            return (
              <li key={ib.id} className="border-t border-border">
                <button className="w-full flex items-center gap-2 px-5 py-2.5 text-left text-[13px] hover:bg-surface-2/50" onClick={() => setOpen(open === ib.id ? null : ib.id)} aria-expanded={open === ib.id}>
                  <ChevronDown className={cx("size-3.5 text-fg-subtle transition-transform", open !== ib.id && "-rotate-90")} />
                  <span className="font-medium font-mono text-[12.5px]">{ib.number}</span>
                  <span className="text-fg-muted truncate">{[ib.supplier, ib.purchaseOrderNumber].filter(Boolean).join(" · ")}</span>
                  <span className="ml-auto shrink-0">{missingN ? <Badge tone="warning">{tn(missingN, "{n} Preis fehlt", "{n} Preise fehlen", { n: missingN })}</Badge> : <Badge tone="success">{t("vollständig")}</Badge>}</span>
                </button>
                {open === ib.id && (
                  <table className="w-full text-[13px] bg-surface-2/40">
                    <tbody>
                      {ib.lines.map((l) => (
                        <tr key={l.id} className="border-t border-border">
                          <td className="pl-11 pr-3 py-2"><span className="font-medium">{l.name}</span><span className="block text-[11.5px] text-fg-subtle font-mono">{l.sku}</span></td>
                          <td className="px-3 text-right text-[12px] text-fg-muted tabular-nums whitespace-nowrap">{t("{r} von {e} gebucht", { r: fmtNumber(l.received), e: fmtNumber(l.expected) })}</td>
                          <td className="px-5 py-2 w-48 text-right">{canEdit ? <LineCostInput inboundId={ib.id} line={l} /> : <span className="tabular-nums">{unitEur(l.unitCost)}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function LineCostInput({ inboundId, line }: { inboundId: string; line: { id: string; sku: string; unitCost: number | null } }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const initial = fieldEur(line.unitCost);
  const [v, setV] = useState(initial);
  useEffect(() => setV(initial), [initial]);
  const save = useMutation({
    mutationFn: (cost: number | null) => api.profit.setInboundLineCost(tenantId, inboundId, line.id, cost),
    onSuccess: () => { toast.success(t("Preis gespeichert")); for (const k of ["profit-inbounds", "profit-costs", "profit"]) void qc.invalidateQueries({ queryKey: [k, tenantId] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const commit = () => {
    if (v === initial) return;
    if (!v.trim()) return save.mutate(null);
    const n = parseEur(v);
    if (n == null) { toast.error(t("Bitte einen Betrag eingeben, z. B. 12,50")); setV(initial); return; }
    save.mutate(n);
  };
  return <Input value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} inputMode="decimal" placeholder="0,00" suffix="€" className="text-right tabular-nums" aria-label={t("Einstandspreis {sku}", { sku: line.sku })} disabled={save.isPending} invalid={line.unitCost == null} />;
}

function SettingsPanel() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const may = usePermission();
  const canEdit = may("profit", "edit");
  const q = useQuery({ queryKey: ["profit-settings", tenantId], queryFn: () => api.profit.settings(tenantId) });
  const st = useQuery({ queryKey: ["profit-status", tenantId], queryFn: () => api.profit.status(tenantId) });
  const [s, setS] = useState<ProfitSettings | null>(null);
  useEffect(() => { if (q.data && !s) setS(q.data.settings); }, [q.data, s]);
  const save = useMutation({
    mutationFn: (v: ProfitSettings) => api.profit.saveSettings(tenantId, v),
    onSuccess: (r) => { setS(r.settings); toast.success(t("Gespeichert")); void qc.invalidateQueries({ queryKey: ["profit", tenantId] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const sync = useMutation({ mutationFn: () => api.profit.sync(tenantId), onSuccess: () => toast.success(t("Abgleich gestartet – dauert ein paar Minuten")), onError: (e) => toast.error(errText(e)) });
  if (!q.data || !s) return <Skeleton className="h-40 max-w-3xl" />;
  const fee = (id: string) => s.channelFees[id] ?? { percent: 0, fixedEur: 0 };
  const setFee = (id: string, k: "percent" | "fixedEur", raw: string) => {
    const n = parseEur(raw) ?? 0;
    setS({ ...s, channelFees: { ...s.channelFees, [id]: { ...fee(id), [k]: n } } });
  };
  return (
    <div className="space-y-4 max-w-3xl">
      <Card className="p-5 space-y-4">
        <div>
          <h2 className="font-semibold text-[14px]">{t("Gebühren je Kanal")}</h2>
          <p className="text-[12px] text-fg-subtle">{t("Zahlungsanbieter (z. B. Shopify Payments, PayPal) und Marktplatz-Provision – Prozent vom Bruttobetrag plus fester Betrag je Bestellung.")}</p>
        </div>
        <div className="space-y-2">
          {q.data.channels.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-2 text-[13px]">
              <span className="flex-1 min-w-40 font-medium">{c.name}</span>
              <div className="w-28"><Input defaultValue={String(fee(c.id).percent).replace(".", ",")} onChange={(e) => setFee(c.id, "percent", e.target.value)} suffix="%" inputMode="decimal" disabled={!canEdit} aria-label={t("Prozent {name}", { name: c.name })} className="text-right" /></div>
              <span className="text-fg-subtle">+</span>
              <div className="w-28"><Input defaultValue={String(fee(c.id).fixedEur).replace(".", ",")} onChange={(e) => setFee(c.id, "fixedEur", e.target.value)} suffix="€" inputMode="decimal" disabled={!canEdit} aria-label={t("Fester Betrag {name}", { name: c.name })} className="text-right" /></div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[13px] pt-2 border-t border-border">
          <span className="flex-1 min-w-40">{t("Steuersatz für Bestellungen ohne Steuerangabe")}<span className="block text-[12px] text-fg-subtle">{t("Shopify liefert die Steuer je Bestellung – für andere Kanäle rechnen wir damit netto.")}</span></span>
          <div className="w-28"><Input defaultValue={String(s.vatRatePct).replace(".", ",")} onChange={(e) => setS({ ...s, vatRatePct: parseEur(e.target.value) ?? 0 })} suffix="%" inputMode="decimal" disabled={!canEdit} aria-label={t("Steuersatz")} className="text-right" /></div>
        </div>
        <label className="flex items-center gap-2 text-[13px]"><input type="checkbox" checked={s.includeShippingRevenue} disabled={!canEdit} onChange={(e) => setS({ ...s, includeShippingRevenue: e.target.checked })} />{t("Versandkosten, die Kunden bezahlen, als Umsatz zählen")}</label>
        {canEdit && <div className="flex justify-end"><Button onClick={() => save.mutate(s)} disabled={save.isPending}>{t("Speichern")}</Button></div>}
      </Card>
      <Card className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h2 className="font-semibold text-[14px]">{t("Umsatzdaten aus den Shops")}</h2>
            <p className="text-[12px] text-fg-subtle">{t("Stündlich: Preise je Position, Rabatte, Steuer, Versand und Erstattungen – getrennt von der Auftragsübernahme.")}</p>
          </div>
          {canEdit && <Button variant="secondary" size="sm" onClick={() => sync.mutate()} disabled={sync.isPending}><RefreshCw className="size-3.5" />{t("Jetzt abgleichen")}</Button>}
        </div>
        <ul className="space-y-1.5 text-[13px]">
          {(st.data?.shops ?? []).map((sh) => (
            <li key={sh.id} className="flex flex-wrap items-center gap-2">
              <span className="font-medium flex-1 min-w-40">{sh.name}</span>
              {sh.exact
                ? sh.lastError
                  ? <Tip content={sh.lastError}><Badge tone="danger">{t("Abgleich gestört")}</Badge></Tip>
                  : <span className="text-fg-muted text-[12.5px]">{sh.lastRunAt ? t("{n} Bestellungen · zuletzt {when}", { n: fmtNumber(sh.orders), when: fmtRelative(sh.lastRunAt) }) : t("wartet auf ersten Abgleich")}</span>
                : <Tip content={t("Für diesen Kanal schätzen wir netto aus der Auftragssumme und dem Steuersatz oben.")}><Badge>{t("geschätzt")}</Badge></Tip>}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ── Weitere Kosten (von Hand) ───────────────────────────────────────

const COST_CATEGORIES = [msg("Marketing"), msg("Software"), msg("Personal"), msg("Miete"), msg("Verpackung"), msg("Versand"), msg("Gebühren"), msg("Sonstiges")];
const thisMonth = () => new Date().toISOString().slice(0, 7);
const monthLabel = (m: string | null) => (m ? new Date(`${m}-15T12:00:00Z`).toLocaleDateString(intlLocale(), { month: "short", year: "numeric" }) : "");

/** Monatliche Fixkosten und Kosten je Auftrag – fließen in Deckungsbeitrag bzw. „Ergebnis nach Fixkosten“ ein */
function ExtraCostsPanel() {
  const { tenantId } = useTenant();
  const may = usePermission();
  const canEdit = may("profit", "edit");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["profit-manual-costs", tenantId], queryFn: () => api.profit.manualCosts(tenantId) });
  const [dialog, setDialog] = useState<{ kind: "monthly" | "order"; item?: ProfitManualCost } | null>(null);
  const del = useMutation({
    mutationFn: (id: string) => api.profit.deleteManualCost(tenantId, id),
    onSuccess: () => { toast.success(t("Gelöscht")); void qc.invalidateQueries({ queryKey: ["profit-manual-costs", tenantId] }); void qc.invalidateQueries({ queryKey: ["profit", tenantId] }); },
  });
  const monthlySum = (q.data?.monthly ?? []).filter((c) => c.startMonth! <= thisMonth() && (!c.endMonth || c.endMonth >= thisMonth())).reduce((s, c) => s + c.amountEur, 0);
  const row = (c: ProfitManualCost) => (
    <tr key={c.id} className="border-b border-border last:border-0">
      <td className="px-5 py-2.5"><span className="font-medium">{t(c.label)}</span>{c.note && <span className="block text-[11.5px] text-fg-subtle">{c.note}</span>}</td>
      <td className="px-3 text-fg-muted">{t(c.category)}</td>
      <td className="px-3 text-fg-muted whitespace-nowrap">{c.kind === "monthly" ? `${monthLabel(c.startMonth)} – ${c.endMonth ? monthLabel(c.endMonth) : t("laufend")}` : <>{c.orderNumber ?? "—"}{c.placedAt && <span className="text-fg-subtle"> · {dmy(c.placedAt)}</span>}</>}</td>
      <td className="px-3 text-right tabular-nums font-medium whitespace-nowrap">{money(Math.round(c.amountEur * 100))}{c.kind === "monthly" && <span className="text-fg-subtle font-normal"> / {t("Monat")}</span>}</td>
      <td className="pr-3 w-20 text-right whitespace-nowrap">
        {canEdit && <>
          <button onClick={() => setDialog({ kind: c.kind, item: c })} className="h-7 px-2 rounded-md text-[12px] text-fg-muted hover:bg-surface-2">{t("Ändern")}</button>
          <button onClick={() => { if (confirm(t("„{name}“ löschen?", { name: c.label }))) del.mutate(c.id); }} className="size-7 inline-grid place-items-center rounded-md text-fg-subtle hover:text-danger" aria-label={t("Löschen")}><Trash2 className="size-3.5" /></button>
        </>}
      </td>
    </tr>
  );
  const table = (items: ProfitManualCost[], empty: string) => items.length ? (
    <div className="overflow-x-auto"><table className="w-full text-[13px] min-w-[560px]"><tbody>{items.map(row)}</tbody></table></div>
  ) : <p className="px-5 pb-4 text-[13px] text-fg-muted">{empty}</p>;

  return (
    <div className="space-y-4 max-w-5xl">
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-start gap-3 px-5 pt-4 pb-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-[14px]">{t("Monatliche Fixkosten")}</h2>
            <p className="text-[12px] text-fg-subtle">{t("Software, Marketing, Personal, Miete … – im Bericht tagesgenau auf den Zeitraum umgelegt und vom Deckungsbeitrag abgezogen („Ergebnis nach Fixkosten“).")}</p>
          </div>
          {monthlySum > 0 && <span className="text-[13px] text-fg-muted">{t("Aktuell")} <span className="font-semibold text-fg tabular-nums">{money(Math.round(monthlySum * 100), 0)}</span> / {t("Monat")}</span>}
          {canEdit && <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setDialog({ kind: "monthly" })}>{t("Fixkosten erfassen")}</Button>}
        </div>
        {!q.data ? <Skeleton className="h-20 mx-5 mb-4" /> : table(q.data.monthly, t("Noch keine Fixkosten erfasst."))}
      </Card>
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-start gap-3 px-5 pt-4 pb-3">
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-[14px]">{t("Kosten je Auftrag")}</h2>
            <p className="text-[12px] text-fg-subtle">{t("Nachversand, Kulanz, Sonderverpackung … – mindern den Deckungsbeitrag genau dieser Bestellung. Auch direkt in der Übersicht über „+“ bei einer Bestellung.")}</p>
          </div>
          {canEdit && <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setDialog({ kind: "order" })}>{t("Kosten erfassen")}</Button>}
        </div>
        {!q.data ? <Skeleton className="h-20 mx-5 mb-4" /> : table(q.data.orders, t("Noch keine Kosten zu Aufträgen erfasst."))}
      </Card>
      {dialog && <CostDialog kind={dialog.kind} {...(dialog.item ? { item: dialog.item } : {})} onClose={() => setDialog(null)} />}
    </div>
  );
}

function CostDialog({ kind, item, order, onClose }: { kind: "monthly" | "order"; item?: ProfitManualCost; order?: { id: string | null; number: string }; onClose: () => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [f, setF] = useState({
    label: item?.label ?? "", category: item?.category ?? (kind === "order" ? "Versand" : "Software"), amount: item ? String(item.amountEur).replace(".", ",") : "",
    startMonth: item?.startMonth ?? thisMonth(), endMonth: item?.endMonth ?? "", orderNumber: item?.orderNumber ?? order?.number ?? "", note: item?.note ?? "",
  });
  // „1.234,56“ (deutsch) oder „6.90“ – mit Komma ist der Punkt Tausendertrenner
  const amountEur = Number(f.amount.includes(",") ? f.amount.replace(/\./g, "").replace(",", ".") : f.amount.trim());
  const body = (): ProfitManualCostInput => kind === "monthly"
    ? { kind, label: f.label, category: f.category, amountEur, startMonth: f.startMonth, endMonth: f.endMonth || null, note: f.note }
    : { kind, label: f.label, category: f.category, amountEur, ...(order?.id && !item ? { orderId: order.id } : { orderNumber: f.orderNumber }), note: f.note };
  const save = useMutation({
    mutationFn: () => (item ? api.profit.updateManualCost(tenantId, item.id, body()) : api.profit.addManualCost(tenantId, body())),
    onSuccess: () => { toast.success(t("Gespeichert")); void qc.invalidateQueries({ queryKey: ["profit-manual-costs", tenantId] }); void qc.invalidateQueries({ queryKey: ["profit", tenantId] }); onClose(); },
    onError: (e) => toast.error(t("Nicht gespeichert"), { description: e instanceof ApiError ? t((e.body as { message?: string })?.message ?? "Fehler") : "" }),
  });
  const label = "block text-[12px] font-medium text-fg-muted mb-1";
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(480px,calc(100vw-32px))] rounded-2xl border border-border bg-surface p-5 shadow-2xl animate-pop-in">
          <Dialog.Title className="text-[16px] font-semibold">{kind === "monthly" ? (item ? t("Fixkosten ändern") : t("Fixkosten erfassen")) : item ? t("Kosten ändern") : order ? t("Kosten zu {n}", { n: order.number }) : t("Kosten zu einem Auftrag")}</Dialog.Title>
          <form className="mt-4 space-y-3" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
            <label className="block"><span className={label}>{t("Bezeichnung")}</span><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder={kind === "monthly" ? t("z. B. Shopify-Abo") : t("z. B. Nachversand")} autoFocus /></label>
            <div className="grid grid-cols-2 gap-3">
              <label><span className={label}>{kind === "monthly" ? t("Betrag je Monat (netto)") : t("Betrag (netto)")}</span><Input value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} inputMode="decimal" suffix="€" /></label>
              <label><span className={label}>{t("Kategorie")}</span>
                <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-9 w-full rounded-lg border border-border bg-surface px-2 text-[13px]">{COST_CATEGORIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}</select>
              </label>
            </div>
            {kind === "monthly" ? (
              <div className="grid grid-cols-2 gap-3">
                <label><span className={label}>{t("Ab Monat")}</span><Input type="month" value={f.startMonth} onChange={(e) => setF({ ...f, startMonth: e.target.value })} /></label>
                <label><span className={label}>{t("Bis Monat (leer = laufend)")}</span><Input type="month" value={f.endMonth} onChange={(e) => setF({ ...f, endMonth: e.target.value })} /></label>
              </div>
            ) : !order || item ? (
              <label className="block"><span className={label}>{t("Auftragsnummer")}</span><Input value={f.orderNumber} onChange={(e) => setF({ ...f, orderNumber: e.target.value })} placeholder="#1001" /></label>
            ) : null}
            <label className="block"><span className={label}>{t("Notiz (optional)")}</span><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></label>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" onClick={onClose}>{t("Abbrechen")}</Button>
              <Button type="submit" variant="primary" loading={save.isPending} disabled={!f.label.trim() || !(amountEur > 0)}>{t("Speichern")}</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
